import React, { useEffect, useState } from 'react';
import { ocorrenciaService } from '../../services/api';
import { showError } from '../../utils/toast';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import FilterChip from '../ui/FilterChip';
import StateBlock from '../ui/StateBlock';
import './Ocorrencias.css';

/**
 * Sessão 24 — Módulo Ocorrências MVP (TD104).
 * #124 (Sessão 35): migrado pros componentes-base (PageHeader/FilterChip/Button/
 * StateBlock) + Material Symbols. Comportamento idêntico.
 */

const STATUS_LABELS = {
  em_triagem: 'Em triagem',
  arquivada: 'Arquivada',
  monitorada: 'Monitorada',
  promovida_nc: 'Promovida → NC',
};

const STATUS_CLASSES = {
  em_triagem: 'oc-badge-triagem',
  arquivada: 'oc-badge-arquivada',
  monitorada: 'oc-badge-monitorada',
  promovida_nc: 'oc-badge-promovida',
};

// #124: emojis removidos das labels (uniformização → texto limpo na tabela).
const ORIGEM_LABELS = {
  reclamacao_cliente: 'Reclamação cliente',
  desvio_interno: 'Desvio interno',
  auditoria: 'Auditoria',
  outro: 'Outro',
};

const STATUS_OPTIONS = [
  { value: 'em_triagem', label: 'Em triagem' },
  { value: 'monitorada', label: 'Monitorada' },
  { value: 'arquivada', label: 'Arquivada' },
  { value: 'promovida_nc', label: 'Promovida → NC' },
];
const ORIGEM_OPTIONS = [
  { value: 'reclamacao_cliente', label: 'Reclamação cliente' },
  { value: 'desvio_interno', label: 'Desvio interno' },
  { value: 'auditoria', label: 'Auditoria' },
  { value: 'outro', label: 'Outro' },
];

function OcorrenciaList({ onSelectOcorrencia, onNew, user }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [origemFiltro, setOrigemFiltro] = useState('todos');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await ocorrenciaService.listar({
        statusTriagem: statusFiltro !== 'todos' ? statusFiltro : undefined,
        origem: origemFiltro !== 'todos' ? origemFiltro : undefined,
        limit: 100,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Erro ao carregar ocorrências:', err);
      showError('Erro ao carregar ocorrências.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFiltro, origemFiltro]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('pt-BR');
    } catch {
      return iso;
    }
  };

  return (
    <div className="oc-list-container">
      <PageHeader
        kicker="Gestão de qualidade"
        title="Ocorrências"
        subtitle="Registre reclamações, desvios menores ou observações de auditoria. Triagem manual — você decide se arquiva, monitora ou promove para NC formal."
        actions={<Button icon="add" onClick={onNew}>Nova ocorrência</Button>}
      />

      <div className="oc-filters">
        <FilterChip
          icon="flag"
          value={statusFiltro}
          onChange={setStatusFiltro}
          placeholder="Status"
          options={STATUS_OPTIONS}
        />
        <FilterChip
          icon="category"
          value={origemFiltro}
          onChange={setOrigemFiltro}
          placeholder="Origem"
          options={ORIGEM_OPTIONS}
        />
      </div>

      {loading ? (
        <StateBlock kind="loading" message="Carregando ocorrências…" />
      ) : items.length === 0 ? (
        <StateBlock
          kind="empty"
          icon="inbox"
          title="Nenhuma ocorrência ainda"
          message="Registre a primeira reclamação, desvio ou observação de auditoria."
          action={<Button icon="add" onClick={onNew}>Registrar a primeira</Button>}
        />
      ) : (
        <div className="oc-table-wrapper">
          <table className="oc-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Origem</th>
                <th>Descrição</th>
                <th className="oc-col-secondary">Reclamante</th>
                <th>Status</th>
                <th className="oc-col-secondary">Criada em</th>
              </tr>
            </thead>
            <tbody>
              {items.map((oc) => (
                <tr
                  key={oc.id}
                  onClick={() => onSelectOcorrencia(oc.id)}
                  className="oc-row-clickable"
                >
                  <td>{oc.numero_ocorrencia || `#${oc.id}`}</td>
                  <td>{ORIGEM_LABELS[oc.origem] || oc.origem}</td>
                  <td className="oc-cell-descricao">
                    {oc.descricao?.length > 80
                      ? oc.descricao.substring(0, 80) + '...'
                      : oc.descricao}
                  </td>
                  <td className="oc-col-secondary">{oc.cliente_reclamante || '—'}</td>
                  <td>
                    <span className={`oc-badge ${STATUS_CLASSES[oc.status_triagem] || ''}`}>
                      {STATUS_LABELS[oc.status_triagem] || oc.status_triagem}
                    </span>
                  </td>
                  <td className="oc-col-secondary">{formatDate(oc.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="oc-total">Total: {total}</p>
        </div>
      )}
    </div>
  );
}

export default OcorrenciaList;
